import React, { useMemo, useState } from 'react';
import { App, Button, Card, Divider, Dropdown, Space, Tag, Tree, Tooltip, Typography } from 'antd';
import { _T } from '../../../../../base-react/src/Localization';
import { AimOutlined, ClockCircleOutlined, CommentOutlined, DownOutlined, EyeInvisibleOutlined, FieldNumberOutlined, LockOutlined, PartitionOutlined, PlusOutlined, SisternodeOutlined, SolutionOutlined, SwapOutlined, TrophyOutlined, PushpinOutlined } from '@ant-design/icons';
import { models, hooks, apis } from "@vote4dance/judging-sdk";
import { RoundPriorityType } from '@vote4dance/judging-types';
import { RoundStatus, RoundType } from '@vote4dance/judging-schema';
import { Link } from 'react-router-dom';
import { groupClassRoundsIntoChildren } from '../../classes/utilities';
import { RoundStatus as RoundStatusComponent } from '../../../../components/RoundStatus';
import { useFederationSettings } from '../../../../../base-react/hooks/Federation';
var RoundItem = models.rounds.RoundItem;
var CompetitionRoundSource = models.rounds.CompetitionRoundSource;
var ClassRoundSource = models.rounds.ClassRoundSource;
var Stream = hooks.streams.Stream;
var Api = apis.Api;
const { Text } = Typography;
const formatRoundTime = (rawTime) => {
    if (!rawTime)
        return undefined;
    const parts = rawTime.split(':');
    if (parts.length >= 2) {
        const hours = parts[0].padStart(2, '0');
        const minutes = parts[1].padStart(2, '0');
        return `${hours}:${minutes}`;
    }
    return rawTime;
};
const getBeatFloorLabel = (round) => {
    const beat = round.round_beat != null ? String(round.round_beat) : '';
    const floor = round.floor_code || round.floor_name || '';
    const parts = [beat, floor].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : undefined;
};
const getRoundMetadata = (round) => {
    var _a;
    const entries = [];
    const timeLabel = formatRoundTime(round.round_estimated_time);
    if (timeLabel) {
        entries.push({
            id: 'time',
            node: (React.createElement(Space, { key: "time", size: 4, align: "center" },
                React.createElement(ClockCircleOutlined, { style: { fontSize: 12, opacity: 0.7 } }),
                React.createElement(Text, { type: "secondary" }, timeLabel))),
        });
    }
    if (round.round_num_heats && round.round_num_heats > 0) {
        entries.push({
            id: 'heats',
            node: (React.createElement(Text, { key: "heats", type: "secondary" }, _T('heat.count', { count: round.round_num_heats }))),
        });
    }
    const comment = (_a = round.round_comment) === null || _a === void 0 ? void 0 : _a.trim();
    if (comment) {
        entries.push({
            id: 'comment',
            node: (React.createElement(Tooltip, { key: "comment", title: comment },
                React.createElement(CommentOutlined, { style: { fontSize: 12, opacity: 0.85 } }))),
        });
    }
    return entries;
};
const getRoundFlags = (round) => {
    const entries = [];
    const bestPlacementValue = Number(round.round_position);
    if (!Number.isNaN(bestPlacementValue) && bestPlacementValue > 0) {
        entries.push({
            id: 'bestPlacement',
            node: (React.createElement(Tooltip, { key: "bestPlacement", title: _T('round.details.round_position.label') },
                React.createElement(Space, { size: 4, align: "center" },
                    React.createElement(AimOutlined, { style: { fontSize: 12, opacity: 0.85 } }),
                    React.createElement(Text, { type: "secondary" }, bestPlacementValue)))),
        });
    }
    if (round.round_hide_results_pos != null && round.round_hide_results_pos >= 0) {
        const hideType = round.round_hide_results_type || 'all';
        entries.push({
            id: 'hideResults',
            node: (React.createElement(Tooltip, { key: "hideResults", title: _T('round.details.round_hide_results_type.' + hideType) },
                React.createElement(Space, { size: 4, align: "center" },
                    React.createElement(EyeInvisibleOutlined, { style: { fontSize: 12, opacity: 0.85 } })))),
        });
    }
    const roundWeight = Number(round.round_weight);
    if (!Number.isNaN(roundWeight) && roundWeight !== 1) {
        entries.push({
            id: 'weight',
            node: (React.createElement(Tooltip, { key: "weightBlock", title: `${_T('round.details.round_weight.label')} ${roundWeight}` },
                React.createElement(FieldNumberOutlined, { style: { fontSize: 12, opacity: 0.85 } }))),
        });
    }
    if (round.round_heat_lock && round.round_heat_lock > 0) {
        entries.push({
            id: 'heatLock',
            node: (React.createElement(Tooltip, { key: "heatLock", title: _T('round.details.round_heat_lock.label') },
                React.createElement(LockOutlined, { style: { fontSize: 12, opacity: 0.85 } }))),
        });
    }
    const prioritySettings = RoundItem.getPrioritySettings(round);
    const priorityType = prioritySettings === null || prioritySettings === void 0 ? void 0 : prioritySettings.type;
    if (priorityType) {
        const priorityIcon = priorityType === RoundPriorityType.Alternates ? SwapOutlined : PartitionOutlined;
        entries.push({
            id: 'priority',
            node: (React.createElement(Tooltip, { key: "priority", title: _T('round.details.round_priority_settings.type.' + priorityType) }, React.createElement(priorityIcon, { style: { fontSize: 12, opacity: 0.85 } }))),
        });
    }
    if (round.panel_id) {
        entries.push({
            id: 'panel',
            node: (React.createElement(Tooltip, { key: "panelOverride", title: _T('panel.extra') },
                React.createElement(SolutionOutlined, { style: { fontSize: 12, opacity: 0.85 } }))),
        });
    }
    return entries;
};
const RoundTree = ({ rounds, selectedRoundId }) => {
    const treeData = rounds ? groupClassRoundsIntoChildren(rounds) : [];
    const renderTitle = (node) => {
        const metadata = getRoundMetadata(node);
        const flags = getRoundFlags(node);
        const beatFloorLabel = getBeatFloorLabel(node);
        const metadataEntries = [...metadata, ...flags];
        const metadataWithSeparators = metadataEntries.flatMap((entry, index) => {
            const parts = [];
            if (entry.id === 'heats' && index === 0) {
                parts.push(React.createElement(Divider, { key: `meta-start-${node.round_id}-${entry.id}`, orientation: "vertical", style: { height: 12, margin: '0 4px' } }));
            }
            parts.push(entry.node);
            if (index < metadataEntries.length - 1) {
                parts.push(React.createElement(Divider, { key: `meta-sep-${node.round_id}-${index}`, orientation: "vertical", style: { height: 12, margin: '0 4px' } }));
            }
            return parts;
        }).filter(Boolean);
        return (React.createElement(Space, { align: "center", wrap: true, size: 4, style: { width: '100%', fontSize: 12 } },
            beatFloorLabel && (React.createElement(Space, { size: 4, align: "center" },
                React.createElement(Text, { type: "secondary" }, beatFloorLabel),
                React.createElement(Divider, { orientation: "vertical", style: { height: 12, margin: '0 4px' } }))),
            React.createElement(Link, { to: `../classes/${node.class_id}/${node.round_id}` }, RoundItem.getRoundLabel(node) || node.round_label),
            React.createElement(Tag, { color: "default", style: {
                    fontSize: 11,
                    padding: '0 6px',
                    fontWeight: 600,
                } }, node.round_count),
            React.createElement(RoundStatusComponent, { round: node }),
            metadataWithSeparators.length > 0 && (React.createElement(Space, { align: "center", size: 0, style: { flexWrap: 'nowrap' } }, metadataWithSeparators))));
    };
    // Collect all keys so we can fully expand the tree by default
    const allKeys = (rounds || []).map((r) => r.round_id);
    return (React.createElement(Tree, { showIcon: true, switcherIcon: React.createElement(DownOutlined, null), treeData: treeData, fieldNames: { title: 'round_label', key: 'round_id' }, titleRender: renderTitle, selectedKeys: selectedRoundId ? [selectedRoundId] : [], defaultExpandAll: true, defaultExpandedKeys: allKeys }));
};
export const RoundClassHierarchy = (props) => {
    const { event_id, class_id, round, classRounds, onAddRound, disabled } = props;
    const { usingPointSummary } = useFederationSettings(round.competition_id);
    const { modal, message } = App.useApp();
    const currentGroupId = Number((round === null || round === void 0 ? void 0 : round.class_group_id) || 0);
    const hasClassRole = !!(round === null || round === void 0 ? void 0 : round.class_role);
    const [allRounds] = Stream(hasClassRole && currentGroupId > 0
        ? CompetitionRoundSource(round.competition_id)
        : undefined);
    const sisterClassId = useMemo(() => {
        if (!(allRounds === null || allRounds === void 0 ? void 0 : allRounds.length) || !currentGroupId)
            return undefined;
        const match = allRounds.find(r => Number(r.class_group_id || 0) === currentGroupId &&
            r.class_id !== class_id);
        return match === null || match === void 0 ? void 0 : match.class_id;
    }, [allRounds, class_id, currentGroupId]);
    const sisterRounds = useMemo(() => {
        if (!(allRounds === null || allRounds === void 0 ? void 0 : allRounds.length) || !sisterClassId)
            return undefined;
        return allRounds.filter(r => r.class_id === sisterClassId);
    }, [allRounds, sisterClassId]);
    const sisterRole = useMemo(() => {
        var _a;
        if (!(sisterRounds === null || sisterRounds === void 0 ? void 0 : sisterRounds.length))
            return undefined;
        return ((_a = sisterRounds[0]) === null || _a === void 0 ? void 0 : _a.class_role) || null;
    }, [sisterRounds]);
    const finalRounds = useMemo(() => {
        if (!(allRounds === null || allRounds === void 0 ? void 0 : allRounds.length) || !currentGroupId)
            return undefined;
        return allRounds.filter(r => parseInt(r.class_id) === currentGroupId);
    }, [allRounds, currentGroupId]);
    // --- Sync Heats modal with local loading ---
    const [syncLoading, setSyncLoading] = useState(false);
    const onSyncHeats = (e) => {
        const targetRoundId = e === null || e === void 0 ? void 0 : e.key;
        const targetRound = sisterRounds === null || sisterRounds === void 0 ? void 0 : sisterRounds.find(r => r.round_id === targetRoundId);
        const targetRoundName = (targetRound === null || targetRound === void 0 ? void 0 : targetRound.round_label) || '';
        modal.confirm({
            title: _T('team.merge.sync-heats', { name: targetRoundName }),
            content: (React.createElement(Space, { orientation: "vertical" },
                React.createElement(Space, null,
                    round.round_label,
                    React.createElement(SwapOutlined, null),
                    targetRoundName),
                _T('common.confirm'))),
            centered: true,
            okButtonProps: { loading: syncLoading },
            onOk: async () => {
                try {
                    setSyncLoading(true);
                    await Api.call(`${ClassRoundSource(round.class_id).url}/${round.round_id}`, "sync-heats", { target_round_id: targetRoundId });
                    message.success(_T('common.apply.success', { name: _T('heat.number').toLowerCase() }));
                }
                finally {
                    setSyncLoading(false);
                }
            },
        });
    };
    const syncItems = ((sisterRounds === null || sisterRounds === void 0 ? void 0 : sisterRounds.filter(a => a.round_type === RoundType.Normal)) || []).map(r => {
        var _a, _b, _c, _d;
        const sameBeat = String((_a = r.round_beat) !== null && _a !== void 0 ? _a : '') === String((_b = round.round_beat) !== null && _b !== void 0 ? _b : '');
        const sameLabel = String((_c = r.round_label) !== null && _c !== void 0 ? _c : '') === String((_d = round.round_label) !== null && _d !== void 0 ? _d : '');
        const showIcon = sameBeat || sameLabel;
        return {
            key: r.round_id,
            disabled: ![RoundStatus.Open, RoundStatus.Ready].includes(r.round_status),
            label: (React.createElement(Space, { size: 6 },
                React.createElement("span", null, r.round_label),
                showIcon && React.createElement(PushpinOutlined, { style: { fontSize: 14, opacity: 0.8 } }))),
        };
    });
    const syncMenuProps = {
        items: syncItems,
        onClick: onSyncHeats,
    };
    const bottomMenuItems = usingPointSummary
        ? [{ label: _T('round.points'), key: RoundType.Points }]
        : [];
    const onBottomMenuClick = (e) => {
        if (e.key === String(RoundType.Points))
            onAddRound(RoundType.Points);
    };
    const childRoundMenuItems = [
        { label: _T('round.child'), key: RoundType.Normal },
        { label: _T('round.chance'), key: RoundType.Chance },
        { label: _T('round.matchrace'), key: 'matchrace' },
    ];
    const onChildMenuClick = (e) => {
        const canAddChild = round.round_type === RoundType.Normal;
        if (!round.round_id || !canAddChild)
            return;
        const key = e.key;
        if (key === RoundType.Normal)
            onAddRound(RoundType.Normal, round);
        if (key === RoundType.Chance)
            onAddRound(RoundType.Chance, round);
        if (key === 'matchrace')
            onAddRound('matchrace', round);
    };
    const renderAddChildButton = () => {
        const canAddChild = round.round_type === RoundType.Normal;
        return (React.createElement(Dropdown, { menu: { items: childRoundMenuItems, onClick: onChildMenuClick }, disabled: disabled || !canAddChild, trigger: ['click'] },
            React.createElement(Button, { type: "dashed", disabled: disabled || !canAddChild }, _T('common.add', { name: _T('round.child').toLowerCase() }))));
    };
    const renderAddRoundButton = () => {
        if (bottomMenuItems.length > 0) {
            return (React.createElement(Dropdown.Button, { type: "dashed", menu: { items: bottomMenuItems, onClick: onBottomMenuClick }, onClick: () => onAddRound(RoundType.Normal), disabled: disabled, icon: React.createElement(DownOutlined, null) }, _T('common.add', { name: _T('round.title').toLowerCase() })));
        }
        return (React.createElement(Button, { type: "dashed", onClick: () => onAddRound(RoundType.Normal), disabled: disabled, icon: React.createElement(PlusOutlined, null) }, _T('common.add', { name: _T('round.title').toLowerCase() })));
    };
    return (React.createElement(Card, { title: React.createElement(React.Fragment, null,
            React.createElement(SisternodeOutlined, null),
            " ",
            _T('round.hierarchy')), size: "small", style: { height: '100%', display: 'flex', flexDirection: 'column' }, styles: { body: { display: 'flex', flexDirection: 'column', flex: 1 } } },
        React.createElement("div", { style: { flex: 1, overflowY: 'auto', paddingBottom: 12 } },
            React.createElement(RoundTree, { rounds: classRounds, selectedRoundId: round.round_id }),
            hasClassRole && currentGroupId > 0 && sisterClassId && (React.createElement(React.Fragment, null,
                React.createElement(Divider, { style: { marginTop: 12, marginBottom: 12 } },
                    React.createElement(Space, null,
                        React.createElement(SisternodeOutlined, null),
                        _T('danceteam.details.dance_role.' + (sisterRole || 'none')))),
                React.createElement(RoundTree, { rounds: sisterRounds }))),
            hasClassRole && currentGroupId > 0 && (finalRounds === null || finalRounds === void 0 ? void 0 : finalRounds.length) ? (React.createElement(React.Fragment, null,
                React.createElement(Divider, { style: { marginTop: 12, marginBottom: 12 } },
                    React.createElement(Space, null,
                        React.createElement(TrophyOutlined, null),
                        _T('round.final'))),
                React.createElement(RoundTree, { rounds: finalRounds }))) : null),
        React.createElement(Divider, { style: { margin: '12px 0' } }),
        React.createElement("div", { style: { textAlign: 'center', paddingBottom: 8 } },
            React.createElement(Space, { wrap: true, size: "middle" },
                hasClassRole && currentGroupId > 0 && (React.createElement(Dropdown, { menu: syncMenuProps },
                    React.createElement(Button, { type: "primary", disabled: disabled || ![RoundStatus.Open, RoundStatus.Ready].includes(round.round_status) },
                        React.createElement(Space, null,
                            _T('team.merge.sync-heats'),
                            React.createElement(DownOutlined, null))))),
                renderAddRoundButton(),
                renderAddChildButton()))));
};
